/**
 * Month and year as plain selects, with arrows to step through.
 *
 * <input type="month"> is not carried by every browser — Safari has never
 * supported it and Firefox renders a bare text box, so the control showed a
 * raw "2026-08" that did not look editable. Two selects behave the same
 * everywhere, and stepping a month at a time is what this page is actually
 * used for.
 */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Ten years back from this one — far enough for any commission question. */
function years(): number[] {
  const now = new Date().getFullYear()
  return Array.from({ length: 11 }, (_, i) => now - i)
}

function shift(month: string, delta: number): string {
  const [year, mon] = month.split('-').map(Number)
  if (!year || !mon) return month
  const date = new Date(year, mon - 1 + delta, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

export function MonthPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (month: string) => void
}) {
  const [year, mon] = value.split('-')
  const monthIndex = Number(mon) - 1
  const yearNumber = Number(year)

  // Nothing after the current month has complete sales in it.
  const now = new Date()
  const atLatest = value >= `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

  function set(nextMonthIndex: number, nextYear: number) {
    onChange(`${nextYear}-${String(nextMonthIndex + 1).padStart(2, '0')}`)
  }

  return (
    <div className="row" style={{ gap: 6 }}>
      <button
        className="btn sm"
        onClick={() => onChange(shift(value, -1))}
        title="Previous month"
        aria-label="Previous month"
      >
        ←
      </button>

      <select
        value={monthIndex}
        aria-label="Month"
        onChange={(event) => set(Number(event.target.value), yearNumber)}
      >
        {MONTHS.map((name, index) => (
          <option key={name} value={index}>
            {name}
          </option>
        ))}
      </select>

      <select
        value={yearNumber}
        aria-label="Year"
        onChange={(event) => set(monthIndex, Number(event.target.value))}
      >
        {years().map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <button
        className="btn sm"
        onClick={() => onChange(shift(value, 1))}
        disabled={atLatest}
        title={atLatest ? 'No later month to show' : 'Next month'}
        aria-label="Next month"
      >
        →
      </button>
    </div>
  )
}
